import React, { useMemo } from 'react';
import { MapContainer, TileLayer, Polyline, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const DIFFICULTY_COLORS = {
  white: '#a3a19a',
  green: '#3a9e3a',
  blue: '#3178c6',
  black: '#2b2a28',
  dh: '#d4453a',
  unknown: '#a3a19a',
};

const COMPLETED_OPACITY = 0.9;
const INCOMPLETE_OPACITY = 0.5;

export default function TrailMap({ trails }) {
  // Only show trails that have coordinates
  const mappable = useMemo(
    () => trails.filter(t => t.coordinates?.coordinates?.length > 1),
    [trails]
  );

  // Convert GeoJSON [lng, lat] to Leaflet [lat, lng]
  const trailLines = useMemo(() =>
    mappable.map(t => ({
      ...t,
      positions: t.coordinates.coordinates.map(([lng, lat]) => [lat, lng]),
    })),
    [mappable]
  );

  // Calculate bounds from all trail coordinates
  const bounds = useMemo(() => {
    if (trailLines.length === 0) return null;
    const allPoints = trailLines.flatMap(t => t.positions);
    const lats = allPoints.map(p => p[0]);
    const lngs = allPoints.map(p => p[1]);
    return [
      [Math.min(...lats), Math.min(...lngs)],
      [Math.max(...lats), Math.max(...lngs)],
    ];
  }, [trailLines]);

  if (trailLines.length === 0) return null;

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: '24px' }}>
      <MapContainer
        bounds={bounds}
        boundsOptions={{ padding: [30, 30] }}
        style={{ height: '400px', width: '100%', borderRadius: 'var(--radius)' }}
        scrollWheelZoom={true}
        attributionControl={false}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />
        {/* Render incomplete trails first so completed ones draw on top */}
        {trailLines
          .sort((a, b) => (a.completed ? 1 : 0) - (b.completed ? 1 : 0))
          .map(trail => (
            <Polyline
              key={trail._id}
              positions={trail.positions}
              pathOptions={{
                color: trail.completed ? '#437326' : DIFFICULTY_COLORS[trail.difficulty] || DIFFICULTY_COLORS.unknown,
                weight: trail.completed ? 4 : 3,
                opacity: trail.completed ? COMPLETED_OPACITY : INCOMPLETE_OPACITY,
                dashArray: trail.completed ? null : '6 4',
              }}
            >
              <Tooltip sticky>
                <span style={{ fontWeight: 600 }}>{trail.name}</span>
                <br />
                <span>{trail.difficulty}{trail.length ? ` · ${trail.length} km` : ''}</span>
                <br />
                <span>{trail.completed ? '✓ Completed' : 'Not ridden'}</span>
              </Tooltip>
            </Polyline>
          ))}
      </MapContainer>
      <div style={{
        display: 'flex', gap: '16px', padding: '10px 16px',
        fontSize: '12px', color: 'var(--text-muted)', flexWrap: 'wrap',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ width: 20, height: 3, background: 'var(--accent)', display: 'inline-block', borderRadius: 2 }} />
          Completed
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ width: 20, height: 3, background: 'var(--neutral-400)', display: 'inline-block', borderRadius: 2, borderTop: '1px dashed var(--neutral-400)' }} />
          Not ridden
        </span>
      </div>
    </div>
  );
}
